import { getOfflineStorageDriver, getEffectiveStorageEngine, OFFLINE_STORES } from './offlineStorageAdapter.js';
import { readStoredAuth } from '../api/authApi.js';
import { requestWithFallback } from '../api/httpClient.js';

const store = OFFLINE_STORES.SHIFTS;
let syncPromise = null;
export const JORNADA_CHANGED = 'jornada:changed';
const notify = () => { if (typeof window !== 'undefined') window.dispatchEvent(new Event(JORNADA_CHANGED)); };
const uuid = () => crypto.randomUUID();

async function storage() {
    const driver = getOfflineStorageDriver();
    await driver.init();
    if (getEffectiveStorageEngine().engine === 'memory') throw new Error('No se pudo abrir el almacenamiento de fichados. Reintentá antes de continuar.');
    return driver;
}

export function gpsFichado(position) {
    if (!position?.coords) return null;
    return { latitude: position.coords.latitude, longitude: position.coords.longitude,
        accuracy: position.coords.accuracy ?? null, captured_at: new Date(position.timestamp || Date.now()).toISOString() };
}

export async function guardarInicioJornada({ userId, startedAt, position, visitaId = null }) {
    const driver = await storage();
    const row = { id: uuid(), userId: Number(userId), revision: uuid(), synced: false,
        inicio: { at: startedAt, gps: gpsFichado(position), visita_id: visitaId }, fin: null, syncError: null, schema_version: 2, pausas: [] };
    await driver.put(store, row);
    notify();
    return row;
}

export async function guardarFinJornada(id, userId, fin) {
    const driver = await storage();
    const updated = await driver.mutate(store, id, (row) => {
        if (!row || row.userId !== Number(userId)) throw new Error('No se encontró el fichado local de esta cuenta.');
        if (row.fin) return row;
        const pausas = (row.pausas || []).map((p) => p.fin ? p : { ...p, fin: fin.at, cierre_automatico: true });
        return { ...row, fin: { ...fin, descanso_minutos: row.schema_version === 2 ? 0 : fin.descanso_minutos }, pausas, revision: uuid(), synced: false, syncError: null };
    });
    notify();
    return updated;
}

export async function iniciarPausaJornada(id, userId, tipo, at = new Date().toISOString()) {
    const driver = await storage();
    const pauseId = uuid();
    const row = await driver.mutate(store, id, (actual) => {
        if (!actual || actual.userId !== Number(userId) || actual.fin || actual.schema_version !== 2) throw new Error('La jornada no admite pausas fichadas.');
        if ((actual.pausas || []).some((p) => !p.fin)) throw new Error('Ya hay una pausa activa. Volvé al trabajo antes de iniciar otra.');
        if (!tipo?.id || !tipo?.politica_version_id || at < actual.inicio.at) throw new Error('Revisá la hora del dispositivo y la configuración de pausas.');
        return { ...actual, pausas: [...(actual.pausas || []), { id: pauseId, tipo_id: tipo.id, politica_version_id: tipo.politica_version_id, inicio: at, fin: null, cierre_automatico: false }], revision: uuid(), synced: false, syncError: null };
    });
    notify(); void sincronizarJornadas(); return row;
}

export async function finalizarPausaJornada(id, userId, pauseId, at = new Date().toISOString()) {
    const driver = await storage();
    const row = await driver.mutate(store, id, (actual) => {
        if (!actual || actual.userId !== Number(userId) || actual.fin) throw new Error('La jornada no está abierta.');
        const pause = (actual.pausas || []).find((p) => p.id === pauseId);
        if (!pause) throw new Error('No se encontró la pausa.');
        if (pause.fin) return actual;
        if (at < pause.inicio) throw new Error('Revisá la hora del dispositivo.');
        return { ...actual, pausas: actual.pausas.map((p) => p.id === pauseId ? { ...p, fin: at } : p), revision: uuid(), synced: false, syncError: null };
    });
    notify(); void sincronizarJornadas(); return row;
}

export async function leerJornadasLocales(userId) {
    const driver = await storage();
    return (await driver.getAll(store, null, { userId: Number(userId) })).filter((row) => row.userId === Number(userId)).sort((a, b) => b.inicio.at.localeCompare(a.inicio.at));
}

export function jornadaSyncError(error) {
    const status = error?.response?.status;
    if (status === 401) return { code: 'unauthenticated', message: 'La sesión venció. Iniciá sesión nuevamente para sincronizar tus fichados.' };
    if (status === 403) return { code: 'forbidden', message: 'Tu cuenta no tiene permiso para sincronizar este fichado. Consultá a un responsable.' };
    if (status === 409 || status === 422) {
        const errors = Object.values(error?.response?.data?.errors || {}).flat();
        return { code: 'validation_error', message: String(errors[0] || error?.response?.data?.message || 'El fichado necesita revisión antes de sincronizarse.') };
    }
    if (error?.code === 'JORNADA_ACK_MISSING') return { code: 'unconfirmed', message: 'El servidor no confirmó el fichado. Se conserva pendiente para reintentar.' };
    if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') return { code: 'timeout', message: 'El servidor demoró demasiado. Tus fichados se conservan para reintentar.' };
    if (!status) return { code: 'offline', message: 'No se pudo conectar con el servidor. Tus fichados se conservan en el dispositivo.' };
    return { code: 'server_error', message: 'El servidor no pudo completar la sincronización. Tus fichados se conservan para reintentar.' };
}

// Una sola vía de envío. El ACK de inicio nunca confirma un cierre que se guardó durante la petición.
export function sincronizarJornadas() {
    if (syncPromise) return syncPromise;
    let changed = false;
    syncPromise = (async () => {
        let userId = null;
        let confirmed = 0;
        const errors = [];
        let pending = null;
        try {
            const session = await readStoredAuth();
            userId = session?.user?.id != null ? Number(session.user.id) : null;
            if (!session?.token || !userId) return { status: 'unauthenticated', confirmed, pending, userId, errors: [], message: 'Iniciá sesión para sincronizar tus fichados.' };
            const driver = await storage();
            const pendientes = (await leerJornadasLocales(userId)).filter((r) => !r.synced).reverse();
            pending = pendientes.length;
            if (typeof navigator !== 'undefined' && !navigator.onLine) {
                return { status: 'offline', confirmed, pending, userId, errors: [], message: 'Estás sin conexión. Tus fichados se conservan en el dispositivo.' };
            }
            for (const row of pendientes) {
                const currentSession = await readStoredAuth();
                if (!currentSession?.token || Number(currentSession?.user?.id) !== userId) {
                    errors.push({ code: 'unauthenticated', message: 'La cuenta cambió o la sesión terminó. Volvé a abrir tus fichados desde tu cuenta.' });
                    break;
                }
                let ackReceived = false;
                try {
                    const { data } = await requestWithFallback({ moduleName: 'jornadas', endpoint: '/react/jornadas/sync', method: 'post',
                        data: { id: row.id, inicio: row.inicio, fin: row.fin, ...(row.schema_version === 2 ? { schema_version: 2, pausas: row.pausas || [] } : {}) }, token: currentSession.token, timeout: 15000 });
                    if (!data?.ok || data.confirmed !== row.id || (row.fin && !data.closed) || (row.schema_version === 2 && data.schema_version !== 2)) {
                        const error = new Error('Falta la confirmación del fichado.');
                        error.code = 'JORNADA_ACK_MISSING';
                        throw error;
                    }
                    ackReceived = true;
                    const updated = await driver.mutate(store, row.id, (actual) => actual?.revision === row.revision && !actual.synced ? { ...actual, synced: true, syncError: null } : null);
                    if (updated?.revision === row.revision && updated.synced) { confirmed++; changed = true; }
                } catch (error) {
                    const failure = ackReceived
                        ? { code: 'storage_error', message: 'No se pudo guardar la confirmación en el dispositivo. El fichado sigue pendiente para reintentar.' }
                        : jornadaSyncError(error);
                    errors.push({ id: row.id, ...failure });
                    const updated = await driver.mutate(store, row.id, (actual) => actual?.revision === row.revision && actual.syncError !== failure.message ? { ...actual, syncError: failure.message } : null);
                    if (updated) changed = true;
                    if (['offline', 'timeout', 'unauthenticated', 'storage_error'].includes(failure.code)) break;
                }
            }
            pending = (await leerJornadasLocales(userId)).filter((r) => !r.synced).length;
            if (!pending) return { status: confirmed ? 'synced' : 'up_to_date', confirmed, pending, userId, errors: [],
                message: confirmed ? `Se ${confirmed === 1 ? 'sincronizó 1 fichado' : `sincronizaron ${confirmed} fichados`}.` : 'No hay fichados pendientes de envío.' };
            const reason = errors[0]?.message || 'Hay cambios nuevos pendientes de sincronización.';
            return { status: confirmed ? 'partial' : errors[0]?.code || 'pending', confirmed, pending, userId, errors,
                message: `${confirmed ? `Se sincronizaron ${confirmed} fichados. ` : ''}${pending === 1 ? 'Queda 1 fichado pendiente' : `Quedan ${pending} fichados pendientes`}. ${reason}` };
        } catch {
            return { status: 'storage_error', confirmed, pending, userId, errors, message: 'No se pudo completar la lectura o escritura de los fichados locales. Reintentá; tus registros no se eliminan.' };
        }
    })().finally(() => { syncPromise = null; if (changed) notify(); });
    return syncPromise;
}
